import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Switch, Spin, Tag } from 'antd';
import { ShieldPlus, Edit2, Trash2 } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { ROLES } from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';

const MODULES = [
  'dashboard',
  'trends',
  'finance',
  'finance_sensitive',
  'hr',
  'sales',
  'support',
  'projects',
  'alerts',
  'users',
  'settings',
];

const MODULE_LABELS = {
  dashboard: 'Dashboard',
  trends: 'Trends',
  finance: 'Finance',
  finance_sensitive: 'Finance — Sensitive Data',
  hr: 'HR',
  sales: 'Sales',
  support: 'Support',
  projects: 'Projects',
  alerts: 'Alerts',
  users: 'Users & Roles',
  settings: 'Settings',
};

const MODULE_DESCRIPTIONS = {
  trends: 'Historical KPI comparisons — WoW / MoM / YoY',
  finance_sensitive: 'Salary, reimbursements, receivables, cash collected, P&L values',
  users: 'Create users, assign roles, manage permissions',
};

const ACTIONS = ['view', 'create', 'edit', 'delete'];

const RolesManager = () => {
  const { fetch, create, update, remove } = useApiMethods();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [form] = Form.useForm();

  const loadRoles = async () => {
    const res = await fetch(ROLES, {}, setLoading);
    if (res?.status !== 'fail') {
      setRoles(res?.data || res?.results || []);
    }
  };

  useEffect(() => {
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initPermissions = (existing = []) => {
    // Backend returns permissions as a list:
    //   [{module, can_view, can_create, can_edit, can_delete}, ...]
    // Convert to the nested object shape the UI uses:
    //   {module: {view, create, edit, delete}}
    const byModule = {};
    if (Array.isArray(existing)) {
      existing.forEach((p) => {
        if (p?.module) {
          byModule[p.module] = {
            view: !!p.can_view,
            create: !!p.can_create,
            edit: !!p.can_edit,
            delete: !!p.can_delete,
          };
        }
      });
    } else if (existing && typeof existing === 'object') {
      // Back-compat: already in the UI shape
      Object.assign(byModule, existing);
    }

    const perms = {};
    MODULES.forEach((mod) => {
      perms[mod] = {};
      ACTIONS.forEach((action) => {
        perms[mod][action] = byModule[mod]?.[action] ?? false;
      });
    });
    return perms;
  };

  const handleSave = async (values) => {
    // Convert permissions {module: {view, create, edit, delete}} → list of
    // {module, can_view, can_create, can_edit, can_delete} that the backend
    // serializer expects. Only include modules with at least one action.
    const permsList = MODULES
      .filter((mod) => {
        const p = permissions[mod];
        return p && (p.view || p.create || p.edit || p.delete);
      })
      .map((mod) => ({
        module: mod,
        can_view: !!permissions[mod]?.view,
        can_create: !!permissions[mod]?.create,
        can_edit: !!permissions[mod]?.edit,
        can_delete: !!permissions[mod]?.delete,
      }));
    const payload = { ...values, permissions: permsList };
    let res;
    if (editingRole) {
      res = await update(`${ROLES}${editingRole.id}/`, payload);
    } else {
      res = await create(ROLES, payload);
    }

    if (res?.status !== 'fail') {
      ToastMsg('success', editingRole ? 'Role updated' : 'Role created');
      setModalOpen(false);
      setEditingRole(null);
      form.resetFields();
      loadRoles();
    } else {
      ToastMsg('error', res?.message || 'Failed to save role');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Role',
      content: 'Are you sure you want to delete this role?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        const res = await remove(`${ROLES}${id}/`);
        if (res?.status !== 'fail') {
          ToastMsg('success', 'Role deleted');
          loadRoles();
        }
      },
    });
  };

  const openEdit = (role) => {
    setEditingRole(role);
    form.setFieldsValue({ name: role.name, description: role.description });
    setPermissions(initPermissions(role.permissions));
    setModalOpen(true);
  };

  const openCreate = () => {
    setEditingRole(null);
    form.resetFields();
    setPermissions(initPermissions());
    setModalOpen(true);
  };

  const togglePerm = (mod, action) => {
    setPermissions((prev) => ({
      ...prev,
      [mod]: {
        ...prev[mod],
        [action]: !prev[mod]?.[action],
      },
    }));
  };

  const columns = [
    { title: 'Role Name', dataIndex: 'name', key: 'name', render: (val) => <span className="font-medium">{val}</span> },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Modules',
      key: 'modules',
      render: (_, record) => {
        const perms = record.permissions || {};
        const activeModules = Object.keys(perms).filter(
          (mod) => Object.values(perms[mod] || {}).some(Boolean)
        );
        return (
          <div className="flex flex-wrap gap-1">
            {activeModules.map((mod) => (
              <Tag key={mod} color="blue" className="text-xs">
                {mod}
              </Tag>
            ))}
            {activeModules.length === 0 && <span className="text-muted-foreground text-xs">None</span>}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button type="text" size="small" icon={<Edit2 size={14} />} onClick={() => openEdit(record)} />
          <Button type="text" size="small" danger icon={<Trash2 size={14} />} onClick={() => handleDelete(record.id)} />
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Roles & Permissions</h2>
          <p className="text-muted-foreground text-sm">Define roles and what each role can access</p>
        </div>
        <Button
          type="primary"
          icon={<ShieldPlus size={16} />}
          onClick={openCreate}
          style={{ backgroundColor: '#4f46e5' }}
        >
          Add Role
        </Button>
      </div>

      <Card className="rounded-xl shadow-sm">
        <Table
          dataSource={roles}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </Card>

      <Modal
        title={editingRole ? 'Edit Role' : 'Create Role'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingRole(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={editingRole ? 'Update' : 'Create'}
        okButtonProps={{ style: { backgroundColor: '#4f46e5' } }}
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="e.g. Finance Manager" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of this role" />
          </Form.Item>
        </Form>

        {/* Permissions matrix */}
        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-3">Permissions</h4>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left px-3 py-2 font-medium">Module</th>
                  {ACTIONS.map((action) => (
                    <th key={action} className="text-center px-3 py-2 font-medium capitalize">
                      {action}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODULES.map((mod) => (
                  <tr key={mod} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">
                      <div>{MODULE_LABELS[mod] || mod}</div>
                      {MODULE_DESCRIPTIONS[mod] && (
                        <div className="text-[11px] font-normal text-muted-foreground mt-0.5">
                          {MODULE_DESCRIPTIONS[mod]}
                        </div>
                      )}
                    </td>
                    {ACTIONS.map((action) => (
                      <td key={action} className="text-center px-3 py-2">
                        <Switch
                          size="small"
                          checked={permissions[mod]?.[action] ?? false}
                          onChange={() => togglePerm(mod, action)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RolesManager;
